import { useEffect, useMemo } from "react";
import type { ScheduleEntry } from "@edu/shared";
import { useOccurrenceChatUnread } from "../occurrence-chat/use-chat-unread.ts";
import {
  useOccurrenceChat,
  type OccurrenceChat,
} from "../occurrence-chat/use-occurrence-chat.ts";
import { chatDotKeys, occurrenceChatAddress } from "./entry-chat.ts";

/**
 * Чат занятия глазами ЭКРАНА РАСПИСАНИЯ: лента открытой карточки и точки
 * непрочитанного в клетках сетки.
 *
 * ⚠️ **Своим файлом, а не двадцатью строками в `SchedulePage`**, потому что тот
 * и без них 600 строк при потолке в 400 (раздел 9 CLAUDE.md). Отдельным он
 * остаётся и по существу: это связка двух РАЗНЫХ опросов, и то, зачем их два,
 * надо было объяснить в одном месте.
 *
 * ⚠️ **Опросов действительно два, и слить их нельзя.** Лента спрашивает
 * страницу открытого чата (сообщения, подписи авторов, окно письма), а точки
 * спрашивают дешёвый счётчик по всей неделе сразу. Один запрос на занятие ради
 * точки означал бы три десятка запросов на неделю, а одна страница на всю
 * неделю не существует вовсе: ключ у каждого занятия свой.
 */
export function useScheduleChat(args: {
  /** Занятия недели, все: точки считаются по тому же набору, что рисует сетка. */
  entries: readonly ScheduleEntry[];
  /** Занятие открытой карточки, либо `null`. */
  selected: ScheduleEntry | null;
  /** Границы недели, локальные даты. Сутки назад дописывает сервер сам. */
  from: string;
  to: string;
  /** Есть ли сессия: спрашивать чаты у того, кто не вошёл, нечем и незачем. */
  enabled: boolean;
}): { chat: OccurrenceChat; dots: ReadonlySet<string> } {
  /**
   * ⚠️ **Адрес считает `occurrenceChatAddress`, а не эта строка.** У сводной
   * строки занятия группы он по ГРУППЕ, у занятия серии по паре, у разового и
   * осиротевшего по уроку, а у заглушки перенесённого занятия его нет вовсе.
   * Правило закрыто тестами, и второго его написания в проекте нет.
   */
  const address = args.selected === null ? null : occurrenceChatAddress(args.selected);
  const chat = useOccurrenceChat({ address, open: address !== null });

  /**
   * ⚠️ **Окно счётчика это ВСЯ неделя, а не видимые колонки.** Неделя и так уже
   * запрошена целиком (правило 4 экрана расписания), а переключение режима
   * «день, 3 дня, неделя» данных не трогает: сузив окно, мы получили бы новый
   * запрос на каждое такое переключение ради точек, которые и так приехали.
   */
  const news = useOccurrenceChatUnread({ from: args.from, to: args.to, enabled: args.enabled });

  const watch = news.watch;
  const refresh = news.refresh;
  useEffect(() => {
    /*
      ⚠️ Открытая карточка называется серверу подсказкой `?id=`: счётчик возит не
      больше полусотни самых свежих занятий, и без подсказки занятие за этой
      чертой не получало бы строки НИКОГДА. Спрашиваем тут же, а не ждём
      следующего захода: точка обязана гаснуть по факту прочтения.
    */
    watch(address);
    refresh();
  }, [address, watch, refresh]);

  const dots = useMemo(() => chatDotKeys(args.entries, news.unreadAt), [args.entries, news]);

  return { chat, dots };
}
